import { useEffect, useMemo } from 'react';

import styled from 'styled-components';
import { Link, useSearchParams } from 'react-router-dom';
import { apolloClient } from '../../App';

import { PAGES } from '../../constants/pages';
import { DELIVERY_STATES_LABELS } from './constants/states';

import {
    DeliveryState,
    useGetDeliveriesQuery,
    useGetOrganizationsQuery,
    useGetUsersByGroupsQuery,
} from 'data/__generated__';
import { DELIVERIES_UPDATED_SUBSCRIPTION } from 'data/subscriptions/delivery';
import { INTERNAL_USER_GROUPS } from 'components/Select/UserSelect';

import { Header, HeaderTitle } from 'components/Header';
import { PageTitle } from 'components/PageTitle';
import { SegmentedControl } from 'components/SegmentedControl';
import { FilterBar, type FilterConfig } from 'components/FilterBar';
import { TotemPrimaryButton } from 'components/TotemPrimaryButton';
import { Loader } from 'components/Loader';

import { DeliveriesViews } from './DeliveriesViews';

import { deliveriesUpdatedHandler } from './utils/deliverySubscriptionsHandlers';
import { dateFromString, formatDateAsAnniversary } from 'helpers/dateTimes';

export type ViewMode = 'kanban' | 'timeline' | 'table';

type DateRangeValue = { gte?: number; lte?: number };

type ParamTypes = {
    searchValue?: string;
    state?: string;
    organizationId?: string;
    deliveryPersonUserId?: string;
    siteId?: string;
    isDynamic?: string;
    startDate?: string;
    endDate?: string;
    view?: ViewMode;
};

type UrlParams<T> = { [P in keyof T]?: string[] };

function parseUrlSearchParams<T>(searchParams: URLSearchParams): UrlParams<T> {
    const params: UrlParams<T> = {};
    // @ts-expect-error URLSearchParams is not templated, key is string even with Location<T>
    searchParams.forEach((value: string, key: keyof T) => {
        if (!params[key]) {
            params[key] = [value];
        } else {
            (params[key] as string[]).push(value);
        }
    });
    return params;
}

function buildUrlSearchParams<T>(params: UrlParams<T>): URLSearchParams {
    return Object.entries(params).reduce((urlSearchParams, [key, values]) => {
        (values as string[]).forEach((value) => urlSearchParams.append(key, value));
        return urlSearchParams;
    }, new URLSearchParams());
}

const STATE_OPTIONS = Object.entries(DELIVERY_STATES_LABELS).map(([value, label]) => ({
    value,
    label,
}));

// The `deliveries(startDate, endDate)` query expects day strings in YYYY-MM-DD format.
function timestampToYmd(timestamp: number): string {
    return formatDateAsAnniversary({ dateTime: new Date(timestamp), useNewFormat: true });
}

export const Deliveries = () => {
    const [searchParams, setSearchParams] = useSearchParams();

    useEffect(() => {
        const observer = apolloClient.subscribe({
            query: DELIVERIES_UPDATED_SUBSCRIPTION,
        });

        const subscription = observer.subscribe(({ data }) => {
            const { deliveriesUpdated } = data;
            deliveriesUpdatedHandler(deliveriesUpdated);
        });

        return () => subscription.unsubscribe();
    }, []);

    const params = parseUrlSearchParams<ParamTypes>(searchParams);
    const viewMode: ViewMode = (params.view?.[0] as ViewMode) || 'kanban';
    // The table view delegates text search to the DataTable's own toolbar, so the page-level search
    // is hidden (and not applied here) to avoid two stacked search boxes. État / Organisation /
    // Site / Livreur / Date filters stay shared across every view.
    const isTableView = viewMode === 'table';

    const startDate = params.startDate?.[0] ?? null;
    const endDate = params.endDate?.[0] ?? null;

    const {
        data: deliveriesData,
        loading: deliveriesLoading,
        error: deliveriesError,
    } = useGetDeliveriesQuery({
        fetchPolicy: 'cache-and-network',
        variables: { startDate, endDate },
    });

    const { data: organizationsData } = useGetOrganizationsQuery({ fetchPolicy: 'cache-and-network' });

    const organizationOptions = useMemo(
        () => (organizationsData?.organizations || []).map(({ _id, name }) => ({ label: name, value: _id })),
        [organizationsData],
    );

    const { data: deliveryPeopleData } = useGetUsersByGroupsQuery({
        variables: { userGroups: INTERNAL_USER_GROUPS },
        fetchPolicy: 'cache-and-network',
    });

    const deliveryPersonOptions = useMemo(
        () =>
            (deliveryPeopleData?.usersByGroups || [])
                .map(({ _id, username, profile }) => ({
                    label: profile?.fullname ? `${profile.fullname} (${username})` : username,
                    value: _id,
                }))
                .sort((a, b) => a.label.localeCompare(b.label)),
        [deliveryPeopleData],
    );

    // Site options are derived from the deliveries actually loaded for the current range:
    // the ids always match what we filter on, and only relevant sites are offered.
    const siteOptions = useMemo(() => {
        const labelBySiteId = new Map<string, string>();
        (deliveriesData?.deliveries || []).forEach((delivery) => {
            if (delivery.siteId && !labelBySiteId.has(delivery.siteId)) {
                labelBySiteId.set(delivery.siteId, delivery.siteName || delivery.totem?.address || delivery.siteId);
            }
        });
        return Array.from(labelBySiteId, ([value, label]) => ({ value, label })).sort((a, b) =>
            a.label.localeCompare(b.label),
        );
    }, [deliveriesData]);

    const searchValue = isTableView ? '' : params.searchValue?.[0] || '';
    const stateFilter = (params.state ?? []) as DeliveryState[];
    const organizationIds = params.organizationId ?? [];
    const deliveryPersonUserIds = params.deliveryPersonUserId ?? [];
    const siteIds = params.siteId ?? [];
    // 'true' | 'false' (stringified Delivery.isDynamic); empty = both fixed and dynamic shown.
    const isDynamicFilter = params.isDynamic ?? [];

    const dateRangeValue: DateRangeValue | undefined =
        startDate || endDate
            ? {
                  gte: startDate ? dateFromString(startDate, true).getTime() : undefined,
                  lte: endDate ? dateFromString(endDate, true).getTime() : undefined,
              }
            : undefined;

    const filterState: Record<string, unknown> = {
        searchValue: params.searchValue?.[0],
        state: stateFilter.length > 0 ? stateFilter : undefined,
        organizationId: organizationIds.length > 0 ? organizationIds : undefined,
        deliveryPersonUserId: deliveryPersonUserIds.length > 0 ? deliveryPersonUserIds : undefined,
        siteId: siteIds.length > 0 ? siteIds : undefined,
        isDynamic: isDynamicFilter.length > 0 ? isDynamicFilter : undefined,
        deliveryDateRange: dateRangeValue,
    };

    const deliveries = useMemo(() => {
        if (!deliveriesData) {
            return [];
        }
        const lowerSearch = searchValue.toLowerCase();
        return deliveriesData.deliveries.filter((delivery) => {
            const matchesSearch =
                !lowerSearch ||
                delivery._id.toLowerCase().includes(lowerSearch) ||
                (delivery.siteId && delivery.siteId.toLowerCase().includes(lowerSearch)) ||
                delivery.organizationName?.toLowerCase().includes(lowerSearch) ||
                delivery.siteName?.toLowerCase().includes(lowerSearch) ||
                delivery.label?.toLowerCase().includes(lowerSearch) ||
                delivery.roundName?.toLowerCase().includes(lowerSearch);

            const matchesState = stateFilter.length === 0 || stateFilter.includes(delivery.state);
            const matchesOrganization =
                organizationIds.length === 0 ||
                (delivery.organizationId ? organizationIds.includes(delivery.organizationId) : false);
            const matchesDeliveryPerson =
                deliveryPersonUserIds.length === 0 ||
                (delivery.deliveryPersonUserId ? deliveryPersonUserIds.includes(delivery.deliveryPersonUserId) : false);
            const matchesSite = siteIds.length === 0 || (delivery.siteId ? siteIds.includes(delivery.siteId) : false);
            const matchesDeliveryType =
                isDynamicFilter.length === 0 || isDynamicFilter.includes(String(delivery.isDynamic));

            return (
                matchesSearch &&
                matchesState &&
                matchesOrganization &&
                matchesDeliveryPerson &&
                matchesSite &&
                matchesDeliveryType
            );
        });
    }, [deliveriesData, searchValue, stateFilter, organizationIds, deliveryPersonUserIds, siteIds, isDynamicFilter]);

    function updateFilter(key: string, value: unknown) {
        const newParams = { ...params };

        // The date-range filter maps onto two URL params (startDate / endDate).
        if (key === 'deliveryDateRange') {
            const range = value as DateRangeValue | undefined;
            delete newParams.startDate;
            delete newParams.endDate;
            if (range?.gte) {
                newParams.startDate = [timestampToYmd(range.gte)];
            }
            if (range?.lte) {
                newParams.endDate = [timestampToYmd(range.lte)];
            }
            setSearchParams(buildUrlSearchParams(newParams));
            return;
        }

        if (value === undefined || value === '' || (Array.isArray(value) && value.length === 0)) {
            delete newParams[key as keyof ParamTypes];
        } else if (Array.isArray(value)) {
            (newParams as Record<string, string[]>)[key] = value;
        } else {
            (newParams as Record<string, string[]>)[key] = [String(value)];
        }
        setSearchParams(buildUrlSearchParams(newParams));
    }

    function clearFilters() {
        const newParams: UrlParams<ParamTypes> = {};
        if (params.view) {
            newParams.view = params.view;
        }
        setSearchParams(buildUrlSearchParams(newParams));
    }

    function updateViewMode(view: ViewMode) {
        setSearchParams(buildUrlSearchParams({ ...params, view: [view] }));
    }

    const filters: FilterConfig[] = [
        {
            key: 'state',
            title: 'État',
            type: 'multi-select',
            options: STATE_OPTIONS,
        },
        {
            key: 'organizationId',
            title: 'Organisation',
            type: 'multi-select',
            options: organizationOptions,
        },
        {
            key: 'siteId',
            title: 'Site',
            type: 'multi-select',
            options: siteOptions,
        },
        {
            key: 'isDynamic',
            title: 'Type de livraison',
            type: 'multi-select',
            options: [
                { value: 'false', label: 'Fixe' },
                { value: 'true', label: 'Dynamique' },
            ],
        },
        {
            key: 'deliveryPersonUserId',
            title: 'Livreur',
            type: 'multi-select',
            options: deliveryPersonOptions,
        },
        {
            key: 'deliveryDateRange',
            title: 'Date de livraison',
            type: 'date-range',
        },
    ];

    if (deliveriesError) {
        throw new Error('Une erreur est survenue lors de la récupération des livraisons');
    }

    return (
        <Container>
            <Header>
                <HeaderTitle>
                    <PageTitle page={PAGES.deliveries} />
                </HeaderTitle>
                <HeaderActions>
                    <SegmentedControl
                        options={[
                            { value: 'kanban' as const, label: 'Kanban' },
                            { value: 'timeline' as const, label: 'Timeline' },
                            { value: 'table' as const, label: 'Tableau' },
                        ]}
                        value={viewMode}
                        onChange={updateViewMode}
                    />
                    <Link to="/deliveries-daily-photos">
                        <TotemPrimaryButton>Contrôle de photos</TotemPrimaryButton>
                    </Link>
                </HeaderActions>
            </Header>
            <FilterBar
                filters={filters}
                filterState={filterState}
                updateFilter={updateFilter}
                clearFilters={clearFilters}
                searchValue={searchValue}
                onSearchChange={isTableView ? undefined : (value) => updateFilter('searchValue', value)}
                searchPlaceholder="Rechercher par ID, site, organisation, label ou tournée…"
            />
            <Content>
                {deliveriesLoading && !deliveriesData ? (
                    <Loader />
                ) : (
                    <DeliveriesViews deliveries={deliveries} viewMode={viewMode} />
                )}
            </Content>
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
    overflow: hidden;
    flex: 1;
    height: 100%;
    background-color: ${({ theme }) => theme.backgroundColor};
`;

const HeaderActions = styled.div`
    display: flex;
    align-items: center;
    gap: 15px;
    margin-left: auto;
`;

const Content = styled.div`
    display: flex;
    flex-direction: column;
    padding: 5px 5px 5px;
    flex: 1;
    overflow: hidden;
    border-radius: 4px;
    margin: 16px;
    background-color: ${({ theme }) => theme.cardBackgroundColor};
`;
